import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject} from '@angular/core';
import {FormArray, UntypedFormGroup} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';

import {CardsApiService, filePath} from '@atmi/core';
import {BaseGuardFormComponent} from '@atmi/core/components/base-guard-form.component';
import {Attachment, cardOA} from '@atmi/core/models';
import {Status} from '@atmi/core/models/status';
import {checkUniqueCodeDuplicate} from '@atmi/core/validators';
import {TranslateService} from '@ngx-translate/core';

import {FormManagerService, keys} from '@sim/form';
import {MediaService, UIMediaService} from '@sim/media';
import {NgxSpinnerService} from 'ngx-spinner';

import {EMPTY, firstValueFrom, Observable} from 'rxjs';
import {filter, first, map, startWith, switchMap, takeUntil} from 'rxjs/operators';

import {Collection} from '@src/app/features/collections/models/collection';
import {CollectionApiService} from '@src/app/features/collections/services/collection-api.service';
import {CollectionItemApiService} from '@src/app/features/collections/services/collection-item-api.service';

export const findInvalidControlsNames = (form: UntypedFormGroup): string[] => {
  const controls = form.controls;
  const invalidControls = [];

  for (const key in controls) {
    if (controls.hasOwnProperty(key)) {
      const control = controls[key];

      if (control.invalid && (control.dirty || control.touched)) {
        invalidControls.push(key);
      }

      if (control instanceof FormArray) {
        for (let i = 0; i < control.length; i++) {
          const formGroup = control.controls[i] as UntypedFormGroup;
          const nestedInvalidControls = findInvalidControlsNames(formGroup);

          if (nestedInvalidControls.length > 0) {
            invalidControls.push(...nestedInvalidControls);
          }
        }
      }
    }
  }
  return invalidControls;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-card-oa-manager',
  styleUrls: ['./card-oa-manager.component.scss'],
  templateUrl: './card-oa-manager.component.html',
})
export class CardOAManagerComponent extends BaseGuardFormComponent<cardOA.CardOA> {

  get imageUrl() {
    const originalUrl = this.formEditData
      ? this.formEditData.previewUrl || ''
      : '';

    return this.formEditData && originalUrl ? this.fileUrl + originalUrl : undefined;
  }

  readonly activatedTabs: Record<number, boolean> = {
    0: true,
    1: true,
    2: false,
    3: false,
    4: false,
    5: true,
    6: true,
    7: false,
    8: false,
  };

  readonly isOperaMadre$: Observable<boolean>;
  readonly isChildCard$: Observable<boolean>;
  
  collections: Array<Collection & { id: string }> = [];
  collectionsLoading = false;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly mediaApi: MediaService,
    private readonly uiMediaService: UIMediaService,
    protected readonly cardsApi: CardsApiService,
    protected readonly cd: ChangeDetectorRef,
    protected readonly formManager: FormManagerService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly snackBarService: MatSnackBar,
    readonly spinner: NgxSpinnerService,
    protected readonly translate: TranslateService,
    private readonly collectionApiService: CollectionApiService,
    private readonly collectionItemApi: CollectionItemApiService,
  ) {
    super(
      snackBarService,
      translate,
      route,
      formManager,
      spinner,
      router,
      cd,
      cardOA.CardOAMeta,
      ((id) => cardsApi.getOA(id as string)),
      '/cards',
      {card: 0},
    );


    this.activatedTabs[this.activeTabIndex] = true;

    this.isOperaMadre$ = this.onFormInit$.pipe(
      filter((form): form is UntypedFormGroup => !!form),
      switchMap((form) => this.entityForm.valueChanges.pipe(startWith(form.value))),
      map((card: cardOA.CardOA) => {
        const isOperaMadreEnable = !!(keys(card.operaMadre).some((key) => this.entityForm.controls.operaMadre.value[key] !== undefined));
        return isOperaMadreEnable;
      }),
    );

    this.isChildCard$ = this.onFormInit$.pipe(
      filter((form): form is UntypedFormGroup => !!form),
      switchMap((form) => this.entityForm.valueChanges.pipe(startWith(form.value))),
      map((card: cardOA.CardOA) => !!card.benePadre),
    );

    this.onFormInit$.pipe(
      filter((form): form is UntypedFormGroup => !!form),
      takeUntil(this.onDestroy$),
    ).subscribe((form) => {
      const limit = this.mode === 'edit' ? 1 : 0;
      form.controls.codiceUnico.setAsyncValidators([checkUniqueCodeDuplicate(this.cardsApi.indexOA.bind(this.cardsApi), limit)]);
      form.controls.codiceUnico.updateValueAndValidity();
      
      // Fetch collections when in edit mode
      if (this.entityId) {
        this.getCollections();
      }
    });
  }

  private async getCollections() {
    this.collectionsLoading = true;
    this.collections = [];
    
    try {
      const res = await firstValueFrom(this.collectionApiService.index().pipe(first()));
      const collections = res.data.filter(c => Boolean(c.id));

      for (const collection of collections) {
        const collectionItems = await firstValueFrom(this.collectionItemApi.index(collection?.id || '').pipe(first()));
        if (collectionItems.data.findIndex((item) => item.objectId === this.entityId) !== -1) {
          this.collections.push(collection as Collection & { id: string });
        }
      }
      
      this.cd.markForCheck();
    } catch (e) {
      console.error(e);
    } finally {
      this.collectionsLoading = false;
      this.cd.markForCheck();
    }
  }

  navigateToCollection(collectionId: string) {
    this.router.navigate(['/collections/manager', collectionId]);
  }

  addConcrete(card: cardOA.CardOA) {
    this.parseCardBeforeUpdate(card);
    return this.cardsApi.postOA(card);
  }

  removeConcrete(card: cardOA.CardOA) {
    if (card.id) {
      return this.cardsApi.removeOA(card.id);
    }
    return EMPTY;
  }

  editConcrete(card: cardOA.CardOA) {
    this.parseCardBeforeUpdate(card);
    return this.cardsApi.putOA(card);
  }

  resetFormConcrete() {
    return this.entityForm.reset();
  }

  changeStatusHandler(status: Status) {
    if (!this.entityControls) {
      throw new Error('Cannot set status without a valid controls');
    }
    if (!this.entityControls.status) {
      throw new Error('Cannot set status without a valid control');
    }
    this.entityControls.status.setValue(status);
    this.entityControls.status.updateValueAndValidity();
  }

  async showMediaHandler() {
    const attachments: Attachment[] | undefined = this.entityControls?.allegati?.value;
    if (!attachments || attachments.length === 0) {
      return;
    }
    const attachmentToShow = attachments[0];
    const media = await firstValueFrom(this.mediaApi.get(attachmentToShow.file).pipe(first()));
    this.uiMediaService.dispatchViewMediaAction(media);
  }

  get originalImageDownloadUrl(): string | undefined {
    const attachments: Attachment[] | undefined = this.entityControls?.allegati?.value;
    if (!attachments || attachments.length === 0) {
      return undefined;
    }
    return this.mediaApi.generateBinaryUrl(attachments[0].file);
  }

  async downloadOriginalImage() {
    const attachments: Attachment[] | undefined = this.entityControls?.allegati?.value;
    if (!attachments || attachments.length === 0) {
      return;
    }
    const fileId = attachments[0].file;
    const blob = await firstValueFrom(this.mediaApi.getBinary(fileId).pipe(first()));
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const baseName = this.entityForm?.value?.codiceUnico || 'download';
    const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/png' ? 'png' : blob.type === 'application/pdf' ? 'pdf' : '';
    a.download = ext ? `${baseName}.${ext}` : baseName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  reloadPage() {
    window.location.reload();
  }

  parseCardBeforeUpdate(card: cardOA.CardOA) {
    if (card.DT.length === 0) {
      card.DT.push({
        'DTM': [],
        'DTS': {
          'DTSF': '',
          'DTSI': ''
        }
      });
    }
    return card;
  }

  findInvalidControlsNames(entityForm: UntypedFormGroup) {
    // Mark all controls as touched to show error states
    this.markAllAsTouched(entityForm);
    return findInvalidControlsNames(entityForm);
  }

  private markAllAsTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormGroup) {
        this.markAllAsTouched(control);
      } else {
        control?.markAsTouched({ onlySelf: true });
      }
    });
  }
}
